import React from 'react';
import Layout from '../../components/Layout';
import Icon from '../../components/AppIcon';

const CareersPage = () => {
  const values = [
    {
      icon: 'Heart',
      title: 'Impact First',
      description: 'We measure success by the positive change we create for micro-businesses and communities.',
    },
    {
      icon: 'Users',
      title: 'Diversity & Inclusion',
      description: 'We build products for diverse communities, so we need diverse perspectives on our team.',
    },
    {
      icon: 'Rocket',
      title: 'Move Fast, Learn Faster',
      description: 'We embrace experimentation and view failures as opportunities to grow.',
    },
    {
      icon: 'Globe',
      title: 'Think Global, Act Local',
      description: 'We build scalable solutions while respecting local culture and context.',
    },
  ];

  const departments = [
    {
      name: 'Engineering',
      description: 'Build the infrastructure powering millions of transactions',
      openings: 4,
      roles: ['Senior Backend Engineer', 'Mobile Developer (React Native)', 'DevOps Engineer', 'Frontend Engineer'],
    },
    {
      name: 'Product',
      description: 'Define and ship features that transform businesses',
      openings: 2,
      roles: ['Product Manager', 'Product Designer'],
    },
    {
      name: 'Operations',
      description: 'Scale our presence across new markets',
      openings: 3,
      roles: ['Market Operations Manager', 'Vendor Success Specialist', 'Customer Support Lead'],
    },
    {
      name: 'Business',
      description: 'Drive partnerships and growth initiatives',
      openings: 2,
      roles: ['Business Development Manager', 'Partnerships Lead'],
    },
  ];

  const benefits = [
    { icon: 'Home', title: 'Remote-First', description: 'Work from anywhere in the Americas' },
    { icon: 'Heart', title: 'Health Coverage', description: 'Medical, dental, and vision plans' },
    { icon: 'GraduationCap', title: 'Learning Budget', description: '$2,000 annual education stipend' },
    { icon: 'Calendar', title: 'Flexible PTO', description: 'Unlimited vacation policy' },
    { icon: 'DollarSign', title: 'Equity', description: 'Stock options for all employees' },
    { icon: 'Laptop', title: 'Equipment', description: 'Latest MacBook and accessories' },
  ];

  const locations = [
    { city: 'Santo Domingo', country: 'Dominican Republic', flag: '🇩🇴', type: 'HQ' },
    { city: 'New York', country: 'USA', flag: '🇺🇸', type: 'Office' },
    { city: 'Miami', country: 'USA', flag: '🇺🇸', type: 'Office' },
    { city: 'Remote', country: 'Americas', flag: '🌎', type: 'Distributed' },
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="py-20 bg-primary text-white">
        <div className="max-w-wide mx-auto px-4 lg:px-8">
          <div className="max-w-3xl">
            <p className="text-sm font-medium text-white/60 uppercase tracking-wider mb-4">
              Careers
            </p>
            <h1 className="text-hero mb-6">
              Build the Future of Local Services
            </h1>
            <p className="text-xl text-white/80 mb-8">
              Join our mission to transform the service economy in Latin America.
              We're looking for passionate people who want to make a real impact.
            </p>
            <a
              href="#openings"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white text-primary font-medium rounded-brand-sm hover:bg-white/90 transition-colors"
            >
              View Open Positions
              <Icon name="ArrowDown" size={16} />
            </a>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-white">
        <div className="max-w-wide mx-auto px-4 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-display text-foreground mb-6">
              Our Values
            </h2>
            <p className="text-body-large">
              These principles guide how we work, make decisions, and build our team.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <div key={index} className="text-center p-6">
                <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name={value.icon} size={28} className="text-accent" />
                </div>
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  {value.title}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section id="openings" className="py-20 bg-muted">
        <div className="max-w-wide mx-auto px-4 lg:px-8">
          <div className="mb-12">
            <h2 className="text-display text-foreground mb-4">
              Open Positions
            </h2>
            <p className="text-muted-foreground">
              Find your role in building the infrastructure for Latin America's service economy.
            </p>
          </div>

          <div className="space-y-6">
            {departments.map((dept, index) => (
              <div key={index} className="bg-white rounded-brand-lg border border-border overflow-hidden">
                <div className="p-6 border-b border-border">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-xl font-semibold text-foreground">{dept.name}</h3>
                      <p className="text-muted-foreground">{dept.description}</p>
                    </div>
                    <span className="px-3 py-1 bg-accent/10 text-accent text-sm font-medium rounded-full">
                      {dept.openings} {dept.openings === 1 ? 'opening' : 'openings'}
                    </span>
                  </div>
                </div>
                <div className="divide-y divide-border">
                  {dept.roles.map((role, i) => (
                    <div key={i} className="p-4 flex items-center justify-between hover:bg-muted transition-colors">
                      <span className="font-medium text-foreground">{role}</span>
                      <button className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-primary hover:bg-primary/5 rounded-brand-sm transition-colors">
                        Apply
                        <Icon name="ArrowRight" size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-white">
        <div className="max-w-wide mx-auto px-4 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-display text-foreground mb-6">
              Benefits & Perks
            </h2>
            <p className="text-body-large">
              We take care of our team so they can focus on building great products.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-start gap-4 p-6 bg-muted rounded-brand">
                <div className="w-12 h-12 bg-primary/10 rounded-brand-sm flex items-center justify-center flex-shrink-0">
                  <Icon name={benefit.icon} size={24} className="text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">{benefit.title}</h3>
                  <p className="text-sm text-muted-foreground">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Locations */}
      <section className="py-20 bg-muted">
        <div className="max-w-wide mx-auto px-4 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h2 className="text-display text-foreground mb-6">
              Where We Work
            </h2>
            <p className="text-body-large">
              We're a distributed team with presence across the Americas.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {locations.map((location, index) => (
              <div key={index} className="bg-white rounded-brand p-6 text-center border border-border">
                <span className="text-4xl mb-4 block">{location.flag}</span>
                <h3 className="font-semibold text-foreground">{location.city}</h3>
                <p className="text-sm text-muted-foreground">{location.country}</p>
                <span className="inline-block mt-3 px-3 py-1 bg-muted text-xs font-medium text-muted-foreground rounded-full">
                  {location.type}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Internships */}
      <section className="py-20 bg-primary">
        <div className="max-w-wide mx-auto px-4 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-display text-white mb-6">
                Internship Program
              </h2>
              <p className="text-white/70 mb-6">
                We partner with universities across the Dominican Republic and Puerto Rico
                to provide hands-on learning experiences for students.
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-2 text-white/80">
                  <Icon name="Check" size={16} className="text-accent-light" />
                  12-week structured program
                </li>
                <li className="flex items-center gap-2 text-white/80">
                  <Icon name="Check" size={16} className="text-accent-light" />
                  Mentorship from senior team members
                </li>
                <li className="flex items-center gap-2 text-white/80">
                  <Icon name="Check" size={16} className="text-accent-light" />
                  Real project ownership
                </li>
                <li className="flex items-center gap-2 text-white/80">
                  <Icon name="Check" size={16} className="text-accent-light" />
                  Paid positions with competitive stipend
                </li>
              </ul>
              <a
                href="mailto:careers@unitedempresaas.com"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white text-primary font-medium rounded-brand-sm hover:bg-white/90 transition-colors"
              >
                Apply for Internship
              </a>
            </div>

            <div className="bg-white/10 backdrop-blur rounded-brand p-8">
              <h3 className="text-lg font-semibold text-white mb-6">
                University Partners
              </h3>
              <div className="space-y-4">
                <div className="flex items-center gap-4 p-3 bg-white/5 rounded-brand-sm">
                  <span className="text-2xl">🇩🇴</span>
                  <div>
                    <div className="font-medium text-white">INTEC</div>
                    <div className="text-sm text-white/60">Santo Domingo</div>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-3 bg-white/5 rounded-brand-sm">
                  <span className="text-2xl">🇩🇴</span>
                  <div>
                    <div className="font-medium text-white">PUCMM</div>
                    <div className="text-sm text-white/60">Santiago</div>
                  </div>
                </div>
                <div className="flex items-center gap-4 p-3 bg-white/5 rounded-brand-sm">
                  <span className="text-2xl">🇵🇷</span>
                  <div>
                    <div className="font-medium text-white">UPR</div>
                    <div className="text-sm text-white/60">San Juan</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-wide mx-auto px-4 lg:px-8 text-center">
          <h2 className="text-display text-foreground mb-6">
            Don't see the right role?
          </h2>
          <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
            We're always looking for talented people who share our mission.
            Send us your resume and tell us how you'd like to contribute.
          </p>
          <a
            href="mailto:careers@unitedempresaas.com"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white font-medium rounded-brand-sm hover:bg-primary-light transition-colors"
          >
            <Icon name="Mail" size={16} />
            careers@unitedempresaas.com
          </a>
        </div>
      </section>
    </Layout>
  );
};

export default CareersPage;
